import type { FocusEventHandler, ReactNode } from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import { Phone } from 'lucide-react';
import { SUPPORTED_COUNTRIES, type SupportedCountryCode } from '@client/util/clerkAuthHelpers';

interface PhoneNumberFieldProps {
  id: string;
  name: string;
  value: {
    countryCode: SupportedCountryCode;
    phoneNumber: string;
  };
  onChange: {
    countryCode: (value: SupportedCountryCode) => void;
    phoneNumber: (value: string) => void;
  };
  validation?: {
    error?: ReactNode;
    invalid?: boolean;
    onBlur?: FocusEventHandler<HTMLDivElement>;
  };
  options?: {
    disabled?: boolean;
    autoFocus?: boolean;
    showLabel?: boolean;
    required?: boolean;
    showHelper?: boolean;
    compact?: boolean;
  };
}

export function PhoneNumberField({
  id,
  name,
  value: { countryCode, phoneNumber },
  onChange,
  validation,
  options,
}: PhoneNumberFieldProps) {
  const { error, invalid, onBlur } = validation ?? {};
  const {
    disabled = false,
    autoFocus,
    showLabel = true,
    required = false,
    showHelper = true,
    compact = false,
  } = options ?? {};
  const selected =
    SUPPORTED_COUNTRIES.find(country => country.code === countryCode) ?? SUPPORTED_COUNTRIES[0];

  if (!selected) return null;

  return (
    <div
      className="space-y-2"
      onBlur={onBlur}
    >
      {showLabel && (
        <label
          htmlFor={id}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          <Phone className="h-4 w-4 text-gray-400" />
          <span>
            Phone number
            {required && (
              <>
                {' '}
                <span className="text-red-700">*</span>
              </>
            )}
          </span>
        </label>
      )}
      <div className="flex gap-2">
        <Select
          value={countryCode}
          onValueChange={value => onChange.countryCode(value as SupportedCountryCode)}
          disabled={SUPPORTED_COUNTRIES.length <= 1 || disabled}
        >
          <SelectTrigger
            className={
              compact
                ? 'focus:border-primary focus:ring-primary h-10! w-30 rounded-lg border-gray-200 disabled:cursor-not-allowed disabled:opacity-60'
                : 'focus:border-primary focus:ring-primary h-12! w-30 rounded-lg border-gray-200 disabled:cursor-not-allowed disabled:opacity-60'
            }
            aria-label="Country code"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_COUNTRIES.map(country => (
              <SelectItem
                key={country.code}
                value={country.code}
              >
                <span className="mr-1">{country.flag}</span>
                {country.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={id}
          name={name}
          type="tel"
          value={phoneNumber}
          placeholder={selected.placeholder}
          autoComplete="off"
          className={
            compact
              ? 'focus:border-primary focus:ring-primary h-10 flex-1 rounded-lg border-gray-200 transition-all duration-200'
              : 'focus:border-primary focus:ring-primary h-12 flex-1 rounded-lg border-gray-200 transition-all duration-200'
          }
          onChange={event => onChange.phoneNumber(event.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          required={required}
          aria-invalid={invalid ?? !!error}
        />
      </div>
      {showHelper && <p className="text-xs text-gray-600">Verification texts only. No spam.</p>}
      {error}
    </div>
  );
}
